/**
 * Error Logging Utility
 * Sends frontend errors to the backend for storage and debugging
 */
import logger from "./logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Get browser information for debugging
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  return {
    userAgent: ua,
    platform: navigator.platform,
    language: navigator.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };
};

/**
 * Get current user from localStorage
 */
const getCurrentUser = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const parsed = JSON.parse(user);
      return {
        username: parsed.username,
        role: parsed.role,
      };
    }
  } catch (e) {
    // Ignore parse errors
  }
  return { username: 'anonymous', role: 'unknown' };
};

/**
 * Log an error to the backend
 * @param {Object} options - Error details
 * @param {Error} options.error - The error object
 * @param {Object} options.errorInfo - React error info with component stack
 * @param {string} options.jobId - Job ID if applicable
 * @param {string} options.jobNumber - Job number (AA-XXXX) if applicable
 * @param {string} options.componentName - Name of the component that failed
 * @param {string} options.page - Current page/route
 * @param {string} options.boundaryType - 'page' or 'section'
 */
export const logError = async ({
  error,
  errorInfo,
  jobId,
  jobNumber,
  componentName,
  page,
  boundaryType = 'unknown'
}) => {
  try {
    const user = getCurrentUser();
    const browserInfo = getBrowserInfo();
    
    const errorLog = {
      timestamp: new Date().toISOString(),
      user: user.username,
      user_role: user.role,
      page: page || window.location.pathname,
      job_id: jobId || null,
      job_number: jobNumber || null,
      component_name: componentName || 'Unknown',
      boundary_type: boundaryType,
      error_message: error?.message || error?.toString() || 'Unknown error',
      error_name: error?.name || 'Error',
      stack_trace: error?.stack || null,
      component_stack: errorInfo?.componentStack || null,
      browser_info: browserInfo,
      url: window.location.href,
    };

    // Get token if available
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API}/errors`, {
      method: 'POST',
      headers,
      body: JSON.stringify(errorLog),
    });

    if (!response.ok) {
      logger.warn('Failed to log error to backend:', response.status);
    }
    
    return true;
  } catch (logError) {
    // Don't throw if logging fails - just console warn
    logger.warn('Error logging failed:', logError);
    return false;
  }
};

/**
 * Global error handler for uncaught errors
 * Attach to window.onerror
 */
export const setupGlobalErrorHandler = () => {
  // Handle uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    logError({
      error: error || new Error(message),
      errorInfo: { componentStack: `Source: ${source}\nLine: ${lineno}, Col: ${colno}` },
      componentName: 'GlobalHandler',
      page: window.location.pathname,
      boundaryType: 'global'
    });
    return false; // Let the error propagate
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    logError({
      error: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      errorInfo: null,
      componentName: 'UnhandledPromiseRejection',
      page: window.location.pathname,
      boundaryType: 'promise'
    });
  };
};

export default { logError, setupGlobalErrorHandler };
